/**
 * Badge Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Siehe: docs/frontend/design-rules.md (Tokens: docs/frontend/design-contract.md)
 */

import React, { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface BadgeProps extends ComponentPropsWithoutRef<'span'> {
  /**
   * Badge variant
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'destructive';

  /**
   * Badge size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Icon on the left side
   */
  icon?: React.ReactNode;

  /**
   * Make badge clickable (renders as button)
   */
  onRemove?: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Badge Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (4.5:1 contrast ratios)
 * - Color-coded status indicators
 * - Icon support
 * - Removable badges (with keyboard support)
 * - Dark/Light mode support
 *
 * @example
 * ```tsx
 * // Status badge
 * <Badge variant="success">Aktiv</Badge>
 *
 * // With icon
 * <Badge variant="warning" icon={<AlertCircle />}>
 *   Beta
 * </Badge>
 *
 * // Removable
 * <Badge onRemove={() => handleRemove()}>
 *   Tag 1
 * </Badge>
 * ```
 */
export const Badge = forwardRef<HTMLSpanElement | HTMLButtonElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'md',
      icon,
      onRemove,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const Component = onRemove ? 'button' : 'span';

    return (
      <Component
        ref={ref as React.Ref<HTMLSpanElement & HTMLButtonElement>}
        className={cn(
          // Base styles
          'inline-flex items-center gap-1',
          'font-medium rounded-full',
          'transition-colors duration-150',

          // Size styles
          {
            'px-2 py-0.5 text-xs': size === 'sm',
            'px-2.5 py-0.5 text-xs': size === 'md',
            'px-3 py-1 text-sm': size === 'lg',
          },

          // Variant styles (WCAG AA: 4.5:1 contrast)
          {
            // Default (Neutral)
            'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300': variant === 'default',

            // Primary (Orange-Marke)
            'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300': variant === 'primary',

            // Secondary (Neutral Dark, Briefing v1.0)
            'bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200': variant === 'secondary',

            // Success (Green)
            'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300': variant === 'success',

            // Warning (Amber)
            'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300': variant === 'warning',

            // Error / Destructive (Red)
            'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300': variant === 'error' || variant === 'destructive',

            // Info (kein Blau lt. Briefing -> helle Orange-Marke)
            'bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300': variant === 'info',
          },

          // Hover state for removable badges
          onRemove && 'hover:opacity-80 cursor-pointer',

          // Focus styles for removable badges
          onRemove && [
            'focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-offset-1',
            'focus-visible:ring-primary-500',
          ],

          // Custom className
          className
        )}
        onClick={onRemove}
        type={onRemove ? 'button' : undefined}
        aria-label={onRemove ? 'Badge entfernen' : undefined}
        {...props}
      >
        {/* Icon */}
        {icon && <span className="shrink-0" aria-hidden="true">{icon}</span>}

        {/* Text */}
        <span>{children}</span>

        {/* Remove Icon */}
        {onRemove && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        )}
      </Component>
    );
  }
);

Badge.displayName = 'Badge';
